import {
  Applicant,
  Application,
  DecisionAction
} from "shared/types/application";
import { Stepper } from "../components/Stepper";
import { Views, FormData } from "../types";
import { ThumbsDown, ThumbsUp } from "untitledui-js-base";
import { CheckboxCard } from "shared/components/forms/CheckboxCard";
import { cx } from "shared/utils/classnames";
import { Dispatch, SetStateAction, lazy, Suspense } from "react";
import { Show, TextArea } from "shared/components";
import { RuleBaseGraph } from "shared/app/RuleBaseGraph";
const ScoreGraph = lazy(
  () =>
    import("views/applications-id/components/ApplicationScoreCard/ScoreGraph")
);
import { ScoreGraphSize } from "views/applications-id/components/ApplicationScoreCard/constants";
import { getFullName } from "shared/utils/get-full-name";
import { Card } from "shared/components/Card";
import { SkippedReportsOverview } from "shared/app/SkippedReportsOverview";
import { useAuthContext } from "providers/Auth";
import { RecommendationMethod } from "types/landlord/verificationTemplate";
import { Loader } from "shared/components/Loader";

const decisionMap = [
  {
    id: DecisionAction.Approve,
    title: "Approve",
    Icon: ThumbsUp,
    iconColor: "stroke-success-500"
  },
  {
    id: DecisionAction.ConditionalApprove,
    title: "Conditional Approve",
    Icon: ThumbsUp,
    iconColor: "stroke-warning-500"
  },
  {
    id: DecisionAction.Decline,
    title: "Decline",
    Icon: ThumbsDown,
    iconColor: "stroke-danger-500"
  }
];
export const MakeDecisionView = ({
  recommendationMethod,
  score,
  recommendation,
  templateCriteriaSettings,
  applicants,
  view,
  flow,
  setFormData,
  onClose,
  formData,
  sendApplicantsToPMS,
  applicationId,
  requiresManualExecution
}: {
  applicationId?: number;
  onClose: () => void;
  requiresManualExecution: Application["requires_manual_or_conditional_execution"];
  recommendationMethod?: RecommendationMethod;
  score: number;
  recommendation?: DecisionAction;
  templateCriteriaSettings: {
    decline_range_end: number;
    conditional_approval_range_end: number;
  };
  applicants: Applicant[];
  view: Views;
  flow: Views[];
  formData: FormData;
  setFormData: Dispatch<SetStateAction<FormData>>;
  sendApplicantsToPMS: Record<DecisionAction, boolean>;
}) => {
  const { permissions, type } = useAuthContext();

  const criteriaEnabled =
    recommendationMethod === "score_based" ||
    recommendationMethod === "rule_based";

  const isCheckboxDisabled = (action: { id: DecisionAction }) => {
    if (!recommendation) return false;

    if (
      type.isAdmin ||
      type.isOwner ||
      permissions.canOverrideDecisionRecommendation
    )
      return false;

    return action.id !== recommendation;
  };

  return (
    <div className="flex flex-col h-full">
      <Stepper view={view} flow={flow} />
      {recommendationMethod === "score_based" ? (
        <div className="px-12 flex gap-8 bg-background-1000  h-[194px] pt-5 border-b">
          <Suspense fallback={<Loader />}>
            <ScoreGraph
              size={ScoreGraphSize.LG}
              subtitle="Application score"
              score={score}
              recommendation={recommendation as DecisionAction}
              declineRangeEnd={templateCriteriaSettings.decline_range_end}
              conditionalApprovalRangeEnd={
                templateCriteriaSettings.conditional_approval_range_end
              }
            />
          </Suspense>
          <div className="h-[146px] border-r border-alpha-100" />
          <div className="flex items-center gap-4 overflow-x-auto">
            {applicants.length
              ? applicants.map((appl) => (
                  <div className="shrink-0" key={appl.id}>
                    <p className="text-sm font-medium text-content-1000">
                      {getFullName({
                        firstName: appl.first_name,
                        lastName: appl.last_name,
                        middleName: appl.middle_name,
                        short: true
                      })}
                    </p>
                  </div>
                ))
              : null}
          </div>
        </div>
      ) : recommendationMethod === "rule_based" ? (
        <div className="flex items-center justify-center flex-col gap-4 border-b border-alpha-100">
          <RuleBaseGraph
            recommendation={recommendation}
            className="h-16 w-16 mt-[50px]"
          />

          <p className="text-base text-content-1000 mb-[50px]">
            Based on your criteria, we recommend{" "}
            {recommendation === DecisionAction.Approve
              ? "approval"
              : recommendation === DecisionAction.Decline
                ? "declining"
                : "conditional approval"}
          </p>
        </div>
      ) : null}
      <div className="bg-background-900 h-full grow-1 p-6 overflow-y-auto">
        <p className="mb-1.5 text-sm font-medium text-content-700">Decision</p>
        <div className="flex gap-4 md:flex-row flex-col">
          {decisionMap.map((action) => {
            return (
              <CheckboxCard
                disabled={isCheckboxDisabled(action)}
                id={action.id}
                name={action.id}
                key={action.id}
                wrapperClassName={
                  criteriaEnabled
                    ? "md:w-[266px] h-[88px]"
                    : "md:w-[240px] h-[194px]"
                }
                className={cx(
                  "flex h-full w-full",
                  !criteriaEnabled ? "items-center justify-center" : ""
                )}
                onChange={() => {
                  setFormData((prev) => ({
                    ...prev,
                    decision: action.id,
                    addApplicantsToPMS: sendApplicantsToPMS[action.id]
                  }));
                }}
                label={
                  criteriaEnabled ? (
                    <>
                      {action.id === recommendation ? (
                        <div
                          className="absolute -top-6 right-2 flex h-6 w-[106px] items-center justify-center rounded-t-lg"
                          style={{
                            background:
                              "linear-gradient(to bottom, #FF49AD -50.23%, #E6007E 93.05%)"
                          }}
                        >
                          <p className="text-xs font-medium text-background-1000">
                            Recommended
                          </p>
                        </div>
                      ) : null}
                      <div className="flex h-full w-full items-center justify-between">
                        <div className="flex items-center gap-2">
                          <action.Icon
                            size="24"
                            pathProps={{
                              className: cx(
                                action.iconColor,
                                isCheckboxDisabled(action) && "stroke-gray-400"
                              )
                            }}
                          />
                          <p className="text-sm font-medium text-alpha-900">
                            {action.title}
                          </p>
                        </div>
                        <div
                          className={cx(
                            "transition:all flex h-4 w-4 items-center justify-center rounded-full",
                            formData.decision === action.id
                              ? "border border-primary-500 bg-primary-20"
                              : "border border-content-100"
                          )}
                        >
                          <div
                            className={
                              formData.decision === action.id
                                ? "h-1.5 w-1.5 rounded-full bg-primary-500"
                                : ""
                            }
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center">
                      <action.Icon
                        size="24"
                        pathProps={{
                          className: cx(
                            action.iconColor,
                            isCheckboxDisabled(action) && "stroke-gray-400"
                          )
                        }}
                      />
                      <p className="text-base font-bold text-alpha-900">
                        {action.title}
                      </p>
                    </div>
                  )
                }
                checked={formData.decision === action.id}
              />
            );
          })}
        </div>

        <div className="mt-4">
          <TextArea
            id="notes"
            label="Notes"
            rows={10}
            className="max-h-[220px] h-full"
            placeholder="You can write a decision note here. The note will not be visible to applicants"
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                decisionNotes: e.target.value
              }));
            }}
            value={formData.decisionNotes}
          />
        </div>

        <Show
          if={
            requiresManualExecution?.total_verifications &&
            requiresManualExecution.total_verifications > 0
          }
        >
          <Card className="p-6">
            <SkippedReportsOverview
              applicationId={applicationId}
              onClose={onClose}
              requiresManualExecution={requiresManualExecution}
            />
          </Card>
        </Show>
      </div>
    </div>
  );
};
